// ---------------------------------------------------------------------------
// PWA Caisse Tablette — useAuthRefresh hook
// Refreshes the JWT access token (HttpOnly cookie) every 13 minutes.
// Access token TTL = 15 min, so 13 min gives a safe 2-minute margin.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/auth-store';
import { authService } from '@/services/auth-service';

/** Refresh interval: 13 minutes (access token is 15 min). */
const REFRESH_INTERVAL_MS = 13 * 60 * 1000;

/**
 * Hook that periodically refreshes the JWT access token when authenticated.
 * If a refresh fails, the user is logged out automatically.
 *
 * Should be used once in the root App component.
 */
export function useAuthRefresh(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer if user logs out
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Start periodic refresh
    timerRef.current = setInterval(async () => {
      try {
        await authService.refresh();
        console.log('[Auth] Token refreshed');
      } catch (err) {
        console.warn('[Auth] Refresh failed:', (err as Error).message);
        // authService.refresh() already calls logout on failure
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAuthenticated]);
}
